import {
  defaultShouldDehydrateQuery,
  environmentManager,
  type Query,
  QueryClient,
} from "@tanstack/react-query";
import { cache } from "react";
import { DEFAULT_GC_TIME_MS, DEFAULT_STALE_TIME_MS } from "@/config/constants";

const serverConfig = {
  defaultOptions: {
    queries: {
      gcTime: DEFAULT_GC_TIME_MS,
      staleTime: DEFAULT_STALE_TIME_MS,
    },
    dehydrate: {
      shouldDehydrateQuery: (query: Query) =>
        defaultShouldDehydrateQuery(query) ||
        query.state.status === "pending" ||
        query.state.status === "error",
    },
  },
};

const clientConfig = {
  defaultOptions: {
    queries: {
      gcTime: DEFAULT_GC_TIME_MS,
      staleTime: DEFAULT_STALE_TIME_MS,
      retry: false,
    },
  },
};

/**
 * Server Component / Server Action 用のリクエストスコープ QueryClient。
 *
 * React の `cache()` により同一リクエスト内では同じインスタンスを返すが、
 * リクエストを跨いでは共有しない。ユーザー・車両単位のデータ（トップ・
 * パッケージ一覧/詳細・注文・車両一覧等）をリクエスト間で共有しないための
 * 設計で、以下の不具合を構造的に防ぐ。
 *   - 別ユーザーのデータが混ざる（VIN未選択時のキー衝突等）
 *   - ECS マルチタスク構成で、無効化操作が処理したタスクにしか効かない
 *   - 無効化のキー組み立てミスで無効化自体が効かない
 *
 * 詳細設計: 実装深堀/キャッシュ戦略/cache-strategy.md §5.1・§6
 */
export const getRequestQueryClient = cache(() => new QueryClient(serverConfig));

let browserQueryClient: QueryClient | undefined;

/**
 * Provider 用の QueryClient。ブラウザでは常に同一のシングルトンを返す。
 *
 * Provider 自体の初回 SSR 描画時にサーバーで呼ばれた場合は、クライアント側
 * シングルトンと共有すると全ユーザーで共有されてしまうため、リクエストごと
 * に新規生成する。
 */
export function getBrowserQueryClient(): QueryClient {
  if (environmentManager.isServer()) {
    return new QueryClient(clientConfig);
  }

  if (!browserQueryClient) {
    browserQueryClient = new QueryClient(clientConfig);
  }

  return browserQueryClient;
}
